"use client";

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Globe } from "lucide-react";
import { DropdownMenuItem } from "@speakai/ui/dropdown-menu";
import { Input } from "@speakai/ui/input";
import { PLATFORM_LANGUAGES } from "@speakai/common/src/config/platform-languages";
import { cn } from "@/lib/utils/cn";

interface LanguageListProps {
  currentLang: string;
  changingLanguage: string | null;
  onSelectLanguage: (code: string) => void;
}

export function LanguageList({ currentLang, changingLanguage, onSelectLanguage }: LanguageListProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");

  if (changingLanguage) {
    return (
      <div role="status" className="flex flex-col items-center gap-2 px-4 py-6 text-center">
        <Globe className="h-6 w-6 animate-spin text-primary" aria-hidden="true" />
        <p className="text-sm font-medium text-foreground">
          {t("SIDEBAR.SETTING_UP_LANGUAGE", { language: changingLanguage })}
        </p>
        <p className="text-xs text-muted-foreground">{t("SIDEBAR.LOADING_TRANSLATIONS")}</p>
      </div>
    );
  }

  const query = search.trim().toLowerCase();
  const languages = PLATFORM_LANGUAGES.filter(
    (lang) => lang.label.toLowerCase().includes(query) || lang.code.toLowerCase().includes(query),
  );

  return (
    <>
      <div className="px-2 pb-1 pt-1">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("SIDEBAR.SEARCH_LANGUAGES")}
          aria-label={t("SIDEBAR.SEARCH_LANGUAGES")}
          className="h-8 text-sm"
        />
      </div>
      <div className="max-h-72 overflow-y-auto" data-testid="language-list-scroll">
        {languages.map((lang) => {
          const isCurrent = lang.code === currentLang;
          return (
            <DropdownMenuItem
              key={lang.code}
              closeOnSelect={false}
              aria-current={isCurrent ? "true" : undefined}
              onClick={() => {
                if (!isCurrent) onSelectLanguage(lang.code);
              }}
              className={cn("py-2", isCurrent && "font-medium text-primary")}
            >
              <span className="flex-1 truncate">{lang.label}</span>
              {isCurrent && <Check className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />}
            </DropdownMenuItem>
          );
        })}
        {languages.length === 0 && (
          <p className="px-3 py-2 text-sm text-muted-foreground">{t("SPOTLIGHT.NO_LANGUAGES_FOUND")}</p>
        )}
      </div>
    </>
  );
}
